import { ContactShadows } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import {
  memo,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { type Mesh, NeutralToneMapping } from "three";
import { enableGeometricSpecularAntiAliasing } from "./geometricSpecularAntiAliasing";
import type { SceneDefinition } from "./Manufacturer";
import {
  type AnyProductStoreHook,
  ProductStoreContext,
} from "./ProductStoreContext";
import {
  ProductViewerPostProcessing,
  useSceneLayers,
} from "./ProductViewerPostProcessing";
import { isSceneDragActive } from "./productDragScope";
import { SceneRenderer } from "./SceneRenderer";
import { StudioFloor } from "./StudioFloor";
import { StudioShadow } from "./StudioShadow";
import { getSceneRevision } from "./sceneActivity";
import {
  fitShadowProxy,
  SHADOW_PROXY_FLAG,
  SHADOW_PROXY_LAYER,
} from "./shadowCasterProxy";
import { readProductBounds } from "./studioFloorExtent";
import { useProductSceneActivity } from "./useProductSceneActivity";

// Shader-chunk patch; must land before the first material compiles, which
// importing this module guarantees for anything the stage renders.
enableGeometricSpecularAntiAliasing();

export const PRODUCT_VIEWER_ENV_RESOLUTION = 512;

/**
 * Global brightness dial for the product scenes. With the HDRI as the only
 * light source this is the one exposure knob — prefer it over touching
 * material or environment intensities.
 */
export const PRODUCT_VIEWER_EXPOSURE = 1;

/**
 * Neutral (KHR) tone mapping instead of R3F's ACES Filmic default. ACES is a
 * film look: it desaturates and hue-shifts saturated highlights, which is wrong
 * for a configurator where the customer is judging their own panel colour.
 * Neutral was designed for product viewing — it keeps hue and saturation and
 * only rolls off the highlights.
 */
const ProductViewerToneMapping = () => {
  const gl = useThree((state) => state.gl);

  useLayoutEffect(() => {
    const previousToneMapping = gl.toneMapping;
    const previousExposure = gl.toneMappingExposure;

    gl.toneMapping = NeutralToneMapping;
    gl.toneMappingExposure = PRODUCT_VIEWER_EXPOSURE;

    return () => {
      gl.toneMapping = previousToneMapping;
      gl.toneMappingExposure = previousExposure;
    };
  }, [gl]);

  return null;
};

/**
 * Height of the ground shadow plane, in metres. Everything the shadow camera
 * can see contributes to the shadow, so anything that must not cast — the floor
 * grid above all — has to sit *below* this. See `GRID_Y` in GridFloorScene.
 *
 * The window is narrow. Above y = 0 the shadow disappears entirely: the blur
 * pass renders a plane that lives at the world origin through this same camera,
 * so lifting the camera puts that plane behind it. Slightly below y = 0 keeps
 * the blur working and keeps floor-aligned geometry clear of the near plane,
 * where it would drop in and out of the shadow from frame to frame.
 */
export const GROUND_SHADOW_Y = -0.002;

/**
 * Ground shadow settings, in metres — the product group is scaled to metres and
 * floor-aligned to y = 0 by {@link ProductFloorAligner}.
 *
 * Image-based lighting cannot occlude anything, so without this the case floats
 * over the backdrop. `scale` covers the footprint of the largest realistic
 * case, `far` how high above the floor geometry still contributes.
 */
const GROUND_SHADOW = {
  scale: 3,
  far: 2,
  blur: 1.2,
  opacity: 0.85,
  /**
   * The shadow is blurred to a soft pool, so it carries almost no detail:
   * quartering the pixels is invisible in the result and matters on an iGPU,
   * where this pass re-renders the whole scene every frame.
   */
  resolution: 256,
  /** drei's `smooth` runs the blur twice; once is enough at this radius. */
  smooth: false,
} as const;

/**
 * Shortest gap between two shadow recaptures, in milliseconds. The shadow is a
 * blurred pool that re-renders the whole scene into its own target, so ~10 Hz
 * is as often as it is worth paying for while geometry is animating.
 */
const GROUND_SHADOW_MIN_INTERVAL_MS = 100;

/**
 * Reuses the shadow while only the camera moves. Changed geometry re-arms
 * drei's one-frame capture without reallocating its render targets.
 *
 * Memoization matters: a parent re-render also resets drei's frame counter.
 */
const GroundShadow = memo(() => {
  const scene = useThree((state) => state.scene);
  const invalidate = useThree((state) => state.invalidate);
  const [revision, setRevision] = useState(() => getSceneRevision(scene));
  const lastUpdate = useRef(Number.NEGATIVE_INFINITY);
  const pending = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (pending.current !== null) clearTimeout(pending.current);
    },
    [],
  );

  useFrame(() => {
    const nextRevision = getSceneRevision(scene);
    if (nextRevision === revision) {
      // Motion may have returned to the already captured revision. Drop the
      // armed wake rather than spending a frame recapturing the same pose —
      // `createStudioShadowRefresh` cancels here for the same reason.
      if (pending.current !== null) clearTimeout(pending.current);
      pending.current = null;
      return;
    }
    // Keep the existing ~10 Hz budget during geometry animation, including
    // one final refresh when it stops between shadow updates.
    //
    // The wait is a timer rather than an `invalidate()`: asking for another
    // frame here would land back in this same branch, and the canvas — which
    // is otherwise `frameloop="demand"` — would spin at full rate, running the
    // whole post-processing chain, for the entire budget window.
    const now = performance.now();
    const remaining =
      GROUND_SHADOW_MIN_INTERVAL_MS - (now - lastUpdate.current);
    if (remaining > 0) {
      pending.current ??= setTimeout(() => {
        pending.current = null;
        lastUpdate.current = performance.now();
        setRevision(getSceneRevision(scene));
      }, remaining);
      return;
    }
    // A normal frame can satisfy the deadline before the timeout fires, so the
    // wake armed earlier would only re-render for a capture already made.
    if (pending.current !== null) clearTimeout(pending.current);
    pending.current = null;
    lastUpdate.current = now;
    setRevision(nextRevision);
  });

  useLayoutEffect(() => {
    void revision;
    invalidate();
  }, [revision, invalidate]);

  // The contact pool is deliberately NOT moved onto the shadow caster proxy.
  // drei reuses this same orthographic camera for its two blur passes
  // (`gl.render(blurPlane, shadowCamera.current)`), and that blur quad is on
  // layer 0 — so restricting the camera to the proxy layer culls the blur as
  // well as the product, and the pool comes out unblurred. The cast shadow has
  // no such coupling: StudioShadow blurs through a camera of its own, so the
  // proxy applies there (see `shadowCasterProxy`).

  return (
    <ContactShadows
      renderOrder={2}
      // Deliberately visible to the AO pass: it is a real horizontal plane
      // at floor level, so occlusion against it deepens the contact shadow
      // where the case meets the ground. (The floor grid is not — see
      // AO_EXCLUDED_LAYER.)
      position={[0, GROUND_SHADOW_Y, 0]}
      scale={GROUND_SHADOW.scale}
      far={GROUND_SHADOW.far}
      blur={GROUND_SHADOW.blur}
      opacity={GROUND_SHADOW.opacity}
      resolution={GROUND_SHADOW.resolution}
      smooth={GROUND_SHADOW.smooth}
      frames={1}
    />
  );
});

interface GroundLayersProps {
  shadowUpdateIntervalMs: number;
  controlledShadowUpdateIntervalMs: number;
}

/**
 * A box on the product's bounds that stands in for it while a gesture is in
 * flight, so the cast shadow does not have to re-render every part. The contact
 * pool keeps the real casters — see the note in {@link GroundShadow}.
 *
 * It lives on {@link SHADOW_PROXY_LAYER} alone, so no camera but a shadow
 * camera that has deliberately switched to that layer can see it — it is not
 * in the beauty pass, not in the ambient-occlusion G-buffer, and not pickable.
 * Rendered here rather than inside the shadow component because this fragment
 * sits at the scene root: the bounds it is fitted to are in world space, so any
 * parent transform would displace it.
 */
const ShadowCasterProxy = () => {
  const scene = useThree((state) => state.scene);
  const meshRef = useRef<Mesh>(null);

  useLayoutEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    mesh.layers.set(SHADOW_PROXY_LAYER);
    // Assigned here rather than through a `userData` prop: a shared literal
    // would be handed to every canvas by reference. `isDragHelper` keeps the
    // box out of `shouldSkipProductBoundsObject`'s walk as well, so a bounds
    // measurement taken from the scene root — rather than from the product
    // group, as every caller does today — cannot feed the proxy its own
    // extent back.
    mesh.userData[SHADOW_PROXY_FLAG] = true;
    mesh.userData.isDragHelper = true;
  }, []);

  // Gated on the gesture, because that is the only state a capture reads the
  // result in: `shouldUseShadowProxy` keeps the real casters otherwise, and
  // this fragment also renders in the preview and thumbnail canvases, which
  // never drag.
  //
  // The priority is the contract, not decoration. The box has to be seated
  // after `ProductFloorAligner` has published the bounds (-0.5) and before
  // `StudioShadow` captures through it (0), and at equal priority that order
  // would rest on nothing but the order of the JSX below — a capture reading a
  // box fitted one frame late trails the case for the whole drag.
  useFrame(() => {
    const mesh = meshRef.current;
    if (!mesh || !isSceneDragActive(scene)) return;
    const bounds = readProductBounds(scene);
    if (!bounds) return;
    fitShadowProxy(mesh, bounds);
  }, -0.25);

  return (
    <mesh ref={meshRef} frustumCulled={false}>
      <boxGeometry args={[1, 1, 1]} />
      {/* Never shaded: every capture replaces it with its own override
          material. Present because a mesh needs one. */}
      <meshBasicMaterial />
    </mesh>
  );
};

/**
 * Blend order: floor patch, grid, contact pool, then cast shadow. The shadow
 * caster proxy draws nothing here; it only stands in for the product in the
 * cast shadow's own capture.
 */
const GroundLayers = ({
  shadowUpdateIntervalMs,
  controlledShadowUpdateIntervalMs,
}: Readonly<GroundLayersProps>) => (
  <>
    <StudioFloor y={GROUND_SHADOW_Y - 0.001} />
    <ShadowCasterProxy />
    <GroundShadow />
    {/* A hair above the pool so the two transparent planes never fight
        for the same depth. */}
    <StudioShadow
      y={GROUND_SHADOW_Y + 0.0005}
      updateIntervalMs={shadowUpdateIntervalMs}
      controlledUpdateIntervalMs={controlledShadowUpdateIntervalMs}
    />
  </>
);

/**
 * The same layers under the viewer's "Boden" toggle. Reading it here rather
 * than in the viewer keeps every canvas that shares a product store — viewer,
 * position preview, PDF/thumbnail capture — showing the same floor, the way
 * `showEdges` and hidden part types already behave. Unlike the diagnostic
 * buffers, a product photographed without its floor is a picture someone chose.
 *
 * It also keeps the flag out of the viewer root, whose part tree would
 * otherwise re-render on every click of the toggle.
 */
const StoreGroundLayers = ({
  store,
  ...props
}: Readonly<GroundLayersProps & { store: AnyProductStoreHook }>) => {
  const showGround = store((state) => state.showGround);
  return showGround ? <GroundLayers {...props} /> : null;
};

interface ProductViewerStageProps {
  /**
   * Optional manufacturer scene (e.g. grid floor). When omitted the standard
   * environment is used without any scene decoration.
   */
  scene?: SceneDefinition;
  resolution?: number;
  /**
   * Studio floor under the product: lit floor patch, soft contact shadow and
   * cast shadow. Off for canvases that show meshes without a floor (mesh
   * editor), where there is nothing to ground against. The store's
   * `showGround` toggle can switch it off on top of this; it cannot switch
   * it back on.
   * @default true
   */
  groundShadow?: boolean;
  /**
   * Screen-space ambient occlusion: contact shading where parts meet, which
   * image-based lighting alone cannot produce. Costs a depth/normal pass plus
   * the AO and denoise passes per frame.
   * @default true
   */
  ambientOcclusion?: boolean;
  /**
   * Lets the product store's `showAmbientOcclusionBuffer` flag put that buffer
   * on screen in place of the composed image. Only the interactive viewer opts in: the
   * preview/thumbnail canvases render this stage under the same store, so
   * without the opt-in a toggle left on in the viewer would be captured and
   * stored as the product image.
   * @default false
   */
  ambientOcclusionDiagnostics?: boolean;
  /** Minimum gap between directional-shadow captures. Zero preserves immediate updates. */
  shadowUpdateIntervalMs?: number;
  /** The same while the user controls the camera. Zero follows every frame. */
  controlledShadowUpdateIntervalMs?: number;
}

/**
 * Single source of truth for product scene lighting + environment. Every canvas
 * (product viewer, mesh editor, preview/thumbnail capture) renders this so the
 * meshes are lit identically and the setups cannot drift apart.
 *
 * All light comes from the scene's HDRI environment map — there are no
 * analytic lights, so nothing can double up on it.
 */
export const ProductViewerStage = ({
  scene,
  resolution = PRODUCT_VIEWER_ENV_RESOLUTION,
  groundShadow = true,
  ambientOcclusion = true,
  ambientOcclusionDiagnostics = false,
  shadowUpdateIntervalMs = 0,
  controlledShadowUpdateIntervalMs = 0,
}: Readonly<ProductViewerStageProps>) => {
  // Independent of the post-processing pass: the helper layer has to be visible
  // to the scene camera even when ambient occlusion is off.
  useSceneLayers();
  useProductSceneActivity();
  // No provider in the mesh editor, which turns the floor off outright.
  const store = useContext(ProductStoreContext);

  return (
    <>
      <ProductViewerToneMapping />
      {ambientOcclusion ? (
        <ProductViewerPostProcessing
          diagnostics={ambientOcclusionDiagnostics}
        />
      ) : null}
      <SceneRenderer scene={scene} resolution={resolution} />
      {groundShadow && store ? (
        <StoreGroundLayers
          store={store}
          shadowUpdateIntervalMs={shadowUpdateIntervalMs}
          controlledShadowUpdateIntervalMs={controlledShadowUpdateIntervalMs}
        />
      ) : null}
      {groundShadow && !store ? (
        <GroundLayers
          shadowUpdateIntervalMs={shadowUpdateIntervalMs}
          controlledShadowUpdateIntervalMs={controlledShadowUpdateIntervalMs}
        />
      ) : null}
    </>
  );
};
